"use client";
/**
 * React wiring for {@link emitTrxFieldsDiagnostic}: a host app mounts
 * {@link TrxFieldsDiagnosticsProvider} once with its error reporter, and every
 * TRX hosted-field component beneath it reports through it. Kept apart from
 * `trxFieldsDiagnostics.ts` so the pure helpers carry no client boundary.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import {
  emitTrxFieldsDiagnostic,
  type TrxFieldsDiagnostic,
  type TrxFieldsDiagnosticHandler,
} from "./trxFieldsDiagnostics";

const TrxFieldsDiagnosticsContext = createContext<TrxFieldsDiagnosticHandler | undefined>(
  undefined,
);

/** Route every TRX hosted-field diagnostic beneath it to the host's reporter. */
export function TrxFieldsDiagnosticsProvider({
  onDiagnostic,
  children,
}: {
  readonly onDiagnostic: TrxFieldsDiagnosticHandler;
  readonly children?: ReactNode;
}) {
  return createElement(
    TrxFieldsDiagnosticsContext.Provider,
    { value: onDiagnostic },
    children,
  );
}

/**
 * A stable reporter for a component: the `onDiagnostic` prop when given, else
 * the nearest {@link TrxFieldsDiagnosticsProvider}, always logged. Stable so it
 * can sit in the (non-idempotent) init effect's dependencies without
 * re-initializing the fields.
 */
export function useTrxFieldsDiagnosticReporter(
  onDiagnostic?: TrxFieldsDiagnosticHandler,
): (diagnostic: TrxFieldsDiagnostic) => void {
  const fromContext = useContext(TrxFieldsDiagnosticsContext);
  const handlerRef = useRef(onDiagnostic ?? fromContext);
  useEffect(() => {
    handlerRef.current = onDiagnostic ?? fromContext;
  }, [onDiagnostic, fromContext]);
  return useCallback(
    (diagnostic: TrxFieldsDiagnostic) =>
      emitTrxFieldsDiagnostic(diagnostic, handlerRef.current),
    [],
  );
}
